import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Card, 
  CardContent,
  Grid,
  CircularProgress,
  Divider,
  Chip,
  useTheme
} from '@mui/material';
import { format } from 'date-fns';
import WashList from './WashList';
import SensorTimeSeries from './SensorTimeSeries';
import useSensorData from '../hooks/useSensorData';

/**
 * View component for displaying wash data and sensor readings around wash events
 */
const WashesView = () => {
  const theme = useTheme();
  const { loading, data, error, selectedTunnel } = useSensorData();
  const [selectedWashDates, setSelectedWashDates] = useState([]);
  
  // Basic statistics
  const totalWashes = data?.washDates?.length || 0;
  const avgDaysBetweenWashes = calculateAverageDays(data?.washDates || []);
  
  function calculateAverageDays(dates) {
    if (!dates || dates.length < 2) return 'N/A';
    
    try {
      // Sort dates chronologically
      const sortedDates = [...dates].sort((a, b) => new Date(a) - new Date(b));
      
      // Calculate differences between consecutive dates in days
      let totalDays = 0;
      for (let i = 1; i < sortedDates.length; i++) {
        const diffTime = Math.abs(new Date(sortedDates[i]) - new Date(sortedDates[i-1]));
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        totalDays += diffDays;
      }
      
      // Calculate average
      return Math.round(totalDays / (sortedDates.length - 1));
    } catch (error) {
      console.error('Error calculating average days:', error);
      return 'N/A';
    }
  }
  
  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Tunnel Wash History
      </Typography>
      
      {selectedTunnel && (
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Showing data for tunnel: <Chip label={selectedTunnel} color="primary" size="small" />
        </Typography>
      )}
      
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Wash Statistics */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Wash Statistics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Washes:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {totalWashes}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Average Days Between Washes:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {avgDaysBetweenWashes}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Last Wash:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data?.washDates?.length > 0 
                      ? format(new Date(Math.max(...data.washDates.map(d => new Date(d)))), 'MMM dd, yyyy')
                      : 'No data'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Wash Timeline */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sensor Values with Wash Events
              </Typography>
              <Box sx={{ height: 300, mt: 2 }}>
                <SensorTimeSeries 
                  data={data?.sensorData || []}
                  washDates={data?.washDates || []}
                  washTypes={data?.washTypes || {}}
                  height={270}
                  showWashLine={true}
                  smooth={false}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Detailed Wash List */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Wash History
              </Typography>
              <WashList tunnel={selectedTunnel} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WashesView;